// @ts-nocheck
// Shared presentational pieces for vulnerability details, used by BOTH the
// drawer (VulnerabilityMainDetails) and the standalone page (VulnerabilityDetails)
// so the two stay in sync. Contains the Confidence indicator and the
// Classification card (CWE + CVSS vectors + EPSS).
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { Card, CardBody, CardHeader } from 'react-bootstrap'
import { Copy, Check } from 'lucide-react'
import { copyToClipboard } from '../../integrations/utils/helpers'
import { parseCvssVector } from '../../../utils/cvss'
import {
  readEpss,
  hasEpssData,
  epssBand,
  epssBandColor,
  epssBandLabelId,
  formatProbabilityPercent,
} from '../../../utils/epss'
import { VulnerabilityFindingDetail } from '../../../modules/types/vulnerabilities'
import { cvssScoreToSeverityLevel, getSeverityBadgeClass } from '../../../utils/severityColors'

// Small copy-to-clipboard button that swaps to a checkmark for ~1.5s after a
// successful copy. Reused for the full CVSS vector strings below.
export const CopyButton = ({ value, label }: { value: string; label?: string }) => {
  const intl = useIntl()
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    const ok = await copyToClipboard(value)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }
  const title = copied ? intl.formatMessage({ id: 'Copied' }) : label ?? intl.formatMessage({ id: 'Copy' })
  return (
    <button
      type='button'
      onClick={handleCopy}
      title={title}
      aria-label={title}
      className={`btn btn-icon btn-sm flex-shrink-0 ${copied ? 'btn-light-success' : 'btn-light-primary'}`}
      style={{ width: '30px', height: '30px' }}
    >
      {copied ? <Check size={15} /> : <Copy size={15} />}
    </button>
  )
}

// EPSS (Exploit Prediction Scoring System) bucket, rendered inside the
// Classification card next to CVSS — the two are complementary, not
// alternatives: CVSS says how bad an exploit would be, EPSS how likely one is.
//
// `score` is shown as an exploitation-risk heat indicator (neutral → red as it
// nears 1) so a high value stands out instantly; `percentile` is a rank, not a
// probability, so it is phrased as "higher than N% of scored CVEs" rather than
// badged like a score. Both raw 0-1 values stay visible beside their percentage
// equivalents, and `epss_cve` names which of the finding's CVEs the numbers
// came from (the backend reports the highest-scoring one, it does not average).
//
// The section is skipped entirely when neither number is scored: a missing EPSS
// means "no score available", never "low risk", so it must not be drawn as a
// zero or a green badge.
const EpssSection = ({ detailData }: { detailData?: VulnerabilityFindingDetail | null }) => {
  const intl = useIntl()
  const { score, percentile, cve } = readEpss(detailData)
  // `0` is a real score — compare against null, never truthiness.
  if (score == null && percentile == null) return null

  const band = epssBand(score)
  const color = epssBandColor(band)
  const bandLabelId = epssBandLabelId(band)

  return (
    <div className='border border-gray-300 rounded overflow-hidden'>
      {/* Header: acronym + plain-language hint, with the risk band on the trailing side */}
      <div className='border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 bg-body px-3 py-2 border-gray-300'>
        <div className='d-flex align-items-center gap-2'>
          <span className='text-gray-900 text-uppercase fw-bold fs-6'>
            {intl.formatMessage({ id: 'EPSS' })}
          </span>
          <span className='text-gray-500 fs-8'>
            {intl.formatMessage({ id: 'Exploitation Probability' })}
          </span>
        </div>
        {score != null && bandLabelId ? (
          <span className={`badge badge-${color} fs-7 text-uppercase`}>
            {intl.formatMessage({ id: bandLabelId })}
          </span>
        ) : null}
      </div>

      {/* Score row: heat bar whose fill + colour intensify with exploitation probability */}
      {score != null ? (
        <div
          className={`d-flex flex-column gap-2 bg-body px-3 py-2 ${
            percentile != null || cve ? 'border-bottom border-gray-300' : ''
          }`}
        >
          <div className='d-flex align-items-center justify-content-between gap-2'>
            <span className='text-gray-500 text-uppercase fs-8 fw-semibold'>
              {intl.formatMessage({ id: 'Score' })}
            </span>
            <div className='d-flex align-items-center gap-2'>
              <code className='text-gray-500 fs-8' dir='ltr'>
                {score}
              </code>
              <span className={`badge badge-${color} fs-6`}>
                {formatProbabilityPercent(score)}
              </span>
            </div>
          </div>
          <div className='bg-secondary rounded overflow-hidden w-100' style={{ height: '8px' }}>
            <div className={`h-100 rounded bg-${color}`} style={{ width: `${score * 100}%` }} />
          </div>
        </div>
      ) : null}

      {/* Percentile row: a RANK, not a probability — read as "higher than N% of
      scored CVEs", so it stays neutral and never wears a severity colour. */}
      {percentile != null ? (
        <div
          className={`d-flex flex-wrap align-items-center justify-content-between gap-2 p-3 ${
            cve ? 'border-bottom bg-body border-gray-300' : ''
          }`}
        >
          <div className='d-flex flex-column' style={{ minWidth: 0 }}>
            <span className='text-gray-500 text-uppercase fs-8 fw-semibold'>
              {intl.formatMessage({ id: 'Percentile' })}
            </span>
            <span className='text-gray-500 fs-8'>
              {intl.formatMessage(
                { id: 'Higher than {percent} of scored CVEs' },
                { percent: formatProbabilityPercent(percentile) }
              )}
            </span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <code className='text-gray-500 fs-8' dir='ltr'>
              {percentile}
            </code>
            <span className='badge badge-light-primary fs-6'>
              {formatProbabilityPercent(percentile)}
            </span>
          </div>
        </div>
      ) : null}

      {/* Attribution row: which CVE the numbers above belong to. A finding can
      list several; the backend reports the highest-scoring one, so naming it
      keeps the score from looking like it covers all of them. Detail only. */}
      {cve ? (
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 p-3'>
          <div className='d-flex flex-column' style={{ minWidth: 0 }}>
            <span className='text-gray-500 text-uppercase fs-8 fw-semibold'>
              {intl.formatMessage({ id: 'Scored CVE' })}
            </span>
            <span className='text-gray-500 fs-8'>
              {intl.formatMessage({ id: 'Highest-scoring CVE on this finding' })}
            </span>
          </div>
          <a
            href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(cve)}`}
            target='_blank'
            rel='noopener noreferrer'
            className='px-3 py-2 text-nowrap badge badge-light-primary fs-6 fw-semibold'
            title={intl.formatMessage({ id: 'CVE' })}
            dir='ltr'
          >
            {cve}
          </a>
        </div>
      ) : null}
    </div>
  )
}

// Detection-confidence indicator (0-100). Renders nothing when no confidence
// score is present. Shown as a colored bar + badge next to the severity.
export const ConfidenceIndicator = ({ confidence }: { confidence?: number | null }) => {
  const intl = useIntl()
  if (confidence == null) return null
  const confidenceColor =
    confidence >= 75 ? 'success' : confidence >= 50 ? 'warning' : 'danger'
  return (
    <div className='d-flex flex-column my-3 my-md-5'>
      <span className='mb-1 text-gray-700 fw-bold'>{intl.formatMessage({ id: 'Confidence' })}</span>
      <div className='d-flex align-items-center gap-2'>
        <div className='bg-secondary rounded overflow-hidden' style={{ height: '6px', width: '110px' }}>
          <div
            className={`h-100 rounded bg-${confidenceColor}`}
            style={{ width: `${Math.max(0, Math.min(100, confidence))}%` }}
          />
        </div>
        <span className={`badge badge-light-${confidenceColor}`}>{confidence}%</span>
      </div>
    </div>
  )
}

// Classification card: CWE links, CVSS classification vectors (dynamic keys), and
// the EPSS section. Renders nothing when there is no classification data and no
// usable EPSS score/percentile.
export const VulnerabilityClassificationCard = ({
  detailData,
}: {
  detailData?: VulnerabilityFindingDetail | null
}) => {
  const intl = useIntl()

  // CVSS classification: dynamic keys (cvss2, cvss4, …). Drop entries whose score is < 0,
  // and parse the raw vector into human-readable metric label/value pairs.
  const classificationEntries = Object.entries(detailData?.classification ?? {})
    .filter(([, entry]) => !(typeof entry?.score === 'number' && entry.score < 0))
    .map(([key, entry]) => ({
      key,
      score: entry?.score ?? null,
      // Preserve the raw vector string so it can be shown in full and copied.
      vector: entry?.vector ?? '',
      parsed: parseCvssVector(entry?.vector),
    }))
  // EPSS lives flat on the payload beside `classification`; show the card when
  // either CVSS entries or a usable EPSS score/percentile is present.
  if (!classificationEntries.length && !hasEpssData(detailData)) return null

  return (
    <Card className='bg-light mt-3'>
      <CardHeader className='align-items-center p-2 border-primary min-h-50px text-center'>
        <h4 className='m-0 p-0 fw-bold'>{intl.formatMessage({ id: 'Classification' })}</h4>
      </CardHeader>
      <CardBody className='p-3'>
        <div className='d-flex flex-column gap-4'>
          {detailData?.classification?.cwe?.length ? (
            <div className='border border-gray-300 rounded overflow-hidden'>
              <div className='border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 bg-body px-3 py-2 border-gray-300'>
                <div className='d-flex align-items-center gap-2'>
                  <span className='text-gray-900 text-uppercase fw-bold fs-6'>
                    {intl.formatMessage({ id: 'CWE' })}
                  </span>
                </div>
              </div>
              <div className='d-flex align-items-center gap-2 p-3'>
                <div className='d-flex flex-wrap gap-2'>
                  {detailData?.classification?.cwe?.map((cwe, i) => {
                    const cweId = cwe.match(/\d+/)?.[0]
                    return cwe ? (
                      <a
                        key={`${cwe}-${i}`}
                        href={`https://cwe.mitre.org/data/definitions/${cweId}.html`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='px-3 py-2 text-nowrap badge badge-light-primary fs-6 fw-semibold'
                        title={intl.formatMessage({ id: 'CWE' })}
                      >
                        {cwe}
                      </a>
                    ) : (
                      <span
                        key={`${cwe}-${i}`}
                        className='px-3 py-2 text-nowrap badge badge-light-primary fs-6 fw-semibold'
                      >
                        {cwe}
                      </span>
                    )
                  })}
                </div>
              </div>
            </div>
          ) : null}
          {classificationEntries.map(({ key, score, vector, parsed }) =>
            key.toLocaleLowerCase() !== 'cwe' ? (
              <div key={key} className='border border-gray-300 rounded overflow-hidden'>
                {/* Bucket header: dynamic key + detected version + score */}
                <div className='border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2 bg-body px-3 py-2 border-gray-300'>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='text-gray-900 text-uppercase fw-bold fs-6'>{key}</span>
                    {parsed?.version && (
                      <span className='badge badge-light-secondary'>v{parsed.version}</span>
                    )}
                  </div>
                  {typeof score === 'number' && (
                    <div className='d-flex align-items-center gap-2'>
                      <span className='text-gray-500 text-uppercase fs-8 fw-semibold'>
                        {intl.formatMessage({ id: 'Score' })}
                      </span>
                      <span
                        className={`badge ${getSeverityBadgeClass(
                          cvssScoreToSeverityLevel(score)
                        )} fs-6`}
                      >
                        {score}
                      </span>
                    </div>
                  )}
                </div>

                {/* Full raw vector string, shown complete (no truncation) with a copy button */}
                {vector ? (
                  <div className='border-bottom d-flex align-items-center justify-content-between gap-3 bg-body px-3 py-2 border-gray-300'>
                    <div className='d-flex flex-column gap-1' style={{ minWidth: 0 }}>
                      <span className='text-gray-500 text-uppercase fs-8 fw-semibold'>
                        {intl.formatMessage({ id: 'Vector' })}
                      </span>
                      <code
                        className='text-gray-900 fs-7'
                        style={{ wordBreak: 'break-all', whiteSpace: 'normal' }}
                        dir='ltr'
                      >
                        {vector}
                      </code>
                    </div>
                    <CopyButton value={vector} label={intl.formatMessage({ id: 'Copy' })} />
                  </div>
                ) : null}

                {/* Metric grid: label above value, one cell per CVSS metric */}
                {parsed?.metrics.length ? (
                  <div
                    className='p-3'
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
                      gap: '10px',
                    }}
                  >
                    {parsed.metrics.map((metric) => (
                      <div key={metric.raw} className='bg-light px-3 py-2 rounded' title={metric.raw}>
                        <div className='mb-1 text-gray-500 text-truncate text-uppercase fs-8 fw-semibold'>
                          {metric.label}
                        </div>
                        <div className='text-gray-900 fw-bold'>{metric.value}</div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null
          )}
          <EpssSection detailData={detailData} />
        </div>
      </CardBody>
    </Card>
  )
}
